"use strict";
var __extends = (this && this.__extends) || function (d, b) {
    for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p];
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
};
var events_1 = require("./events");
var types_1 = require("./types");
var media_streams_1 = require("./media-streams");
var utils_1 = require("./utils");
var DEFAULT_INITIAL_BITRATE = 1750; // 1750kbps, can be modified via Vidi.setInitialBitrate()
/**
 * The main `vidi` class.
 *
 * Each instance manages playback for a single HTMLVideoElement, onto which sources can be loaded.
 */
var Vidi = (function (_super) {
    __extends(Vidi, _super);
    /**
     * Constructor for creating Vidi instances
     *
     * @param nativeVideoEl is an optional parameter and is a shorthand for:
     * ```ts
     * const vidi = new Vidi();
     * vidi.setVideoElement(nativeVideoEl);
     * ```
     */
    function Vidi(nativeVideoEl) {
        if (nativeVideoEl === void 0) { nativeVideoEl = null; }
        var _this = _super.call(this) || this;
        _this.initialBitrate = DEFAULT_INITIAL_BITRATE;
        _this.nativeEventHandlers = events_1.getNativeEventsHandlers(_this);
        _this.playableStreamCreators = [];
        _this.videoElement = null;
        _this.currentSrc = null;
        _this.playableStreams = null;
        _this.attachedStream = null;
        _this.onNativeEvent = _this.onNativeEvent.bind(_this);
        var streamCreators = [
            media_streams_1.getNativeStreamCreator(types_1.MediaStreamTypes.HLS, types_1.MediaStreamDeliveryType.NATIVE_ADAPTIVE),
            media_streams_1.HlsStream,
            media_streams_1.DashStream,
            media_streams_1.getNativeStreamCreator(types_1.MediaStreamTypes.MP4, types_1.MediaStreamDeliveryType.NATIVE_PROGRESSIVE),
            media_streams_1.getNativeStreamCreator(types_1.MediaStreamTypes.WEBM, types_1.MediaStreamDeliveryType.NATIVE_PROGRESSIVE) // Native WebM (Chrome, Firefox)
        ];
        // Only add supported handlers 
        streamCreators.forEach(function (streamCreator) { return streamCreator.isSupported(utils_1.NativeEnvironmentSupport) && _this.playableStreamCreators.push(streamCreator); });
        _this.setVideoElement(nativeVideoEl);
        return _this;
    }
    Object.defineProperty(Vidi.prototype, "src", {
        /**
         * Getter for the src.
         *
         * @returns The currently set [[MediaSource]] on this vidi instance.
         */
        get: function () {
            return this.currentSrc;
        },
        /**
         * Sets a new `src` for playback.
         * If a `<video>` element is already attached, this will initiate loading of the new `src` into it.
         *
         * @param src The new [[MediaSource]].
         *
         */
        set: function (src) {
            if (src === this.currentSrc) {
                return;
            }
            this.detachCurrentStream();
            this.currentSrc = src;
            this.resolvePlayableStreams();
            this.connectStreamToVideo();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Attaches the `vidi` instance to a new `<video>` element.
     * If a previous element was set, `vidi` will detach from it.
     * If a `src` is already set, this will also trigger loading the `src` into the new element.
     *
     * @param nativeVideoEl The new `<video>` element to attach to.
     */
    Vidi.prototype.setVideoElement = function (nativeVideoEl) {
        if (this.videoElement === nativeVideoEl) {
            return;
        }
        this.removeNativeVideoListeners(this.videoElement);
        this.detachCurrentStream();
        this.videoElement = nativeVideoEl;
        this.addNativeVideoListeners(this.videoElement);
        this.connectStreamToVideo();
    };
    /**
     * @returns The currently attached `<video>` element.
     */
    Vidi.prototype.getVideoElement = function () {
        return this.videoElement;
    };
    /**
     * @returns If a `<video>` element is attached, it returns the current [[PlaybackState]] as an object.
     * Otherwise, returns [[defaultPlaybackState]].
     *
     *
     */
    Vidi.prototype.getPlaybackState = function () {
        if (!this.videoElement) {
            return types_1.defaultPlaybackState;
        }
        var playbackState = {
            currentTime: this.videoElement.currentTime,
            duration: this.videoElement.duration || 0,
            muted: this.videoElement.muted,
            playbackRate: this.videoElement.playbackRate,
            status: this.videoElement.paused ? types_1.PlaybackStatus.PAUSED : types_1.PlaybackStatus.PLAYING,
            volume: this.videoElement.volume
        };
        return playbackState;
    };
    /**
     * Same as calling the native `play()` method on the attached `<video>` node,
     * but also handles exceptions and Promise rejections (depending on the browser),
     * and exposes them via the `error` event.
     *
     * *Does nothing when no `<video>` is attached.*
     */
    Vidi.prototype.play = function () {
        if (!this.videoElement) {
            return;
        }
        // Errors will be exposed by the native error handler of vidi
        try {
            var res = this.videoElement.play();
            if (res && res.catch) {
                res.catch(function () { });
            }
        }
        catch (e) {
        }
    };
    /**
     * Same as calling the native `pause()` method on the attached `<video>` node,
     * but also handles exceptions and Promise rejections (depending on the browser),
     * and exposes them via the `error` event.
     *
     * *Does nothing when no `<video>` is attached.*
     */
    Vidi.prototype.pause = function () {
        if (!this.videoElement) {
            return;
        }
        // Errors will be exposed by the native error handler of vidi
        try {
            var res = this.videoElement.pause();
            if (res && res.catch) {
                res.catch(function () { });
            }
        }
        catch (e) {
        }
    };
    /**
     * Sets the preferred initial bitrate for adaptive playback.
     * This will only affect playback via MSE-based libraries, where we have
     * the ability to specify it. Native adaptive playback will not be
     * affected and will still use the browser's default behavior.
     */
    Vidi.prototype.setInitialBitrate = function (bitrate) {
        this.initialBitrate = bitrate;
    };
    /**
     * Sets the current level for the
     */
    Vidi.prototype.setMediaLevel = function (index) {
        if (this.attachedStream && this.videoElement) {
            this.attachedStream.setMediaLevel(index, this.videoElement);
        }
    };
    // Private helpers
    Vidi.prototype.autoDetectSourceTypes = function (mediaSources) {
        return mediaSources.map(function (mediaSource) {
            if (typeof mediaSource === 'string') {
                return { url: mediaSource, type: media_streams_1.detectStreamType(mediaSource) };
            }
            else {
                return mediaSource;
            }
        });
    };
    Vidi.prototype.resolvePlayableStreams = function () {
        if (!this.currentSrc) {
            this.playableStreams = [];
            return;
        }
        var mediaSources = new Array().concat(this.currentSrc);
        var mediaStreams = this.autoDetectSourceTypes(mediaSources);
        this.playableStreams = media_streams_1.resolvePlayableStreams(mediaStreams, this.playableStreamCreators, this.emit.bind(this));
    };
    Vidi.prototype.connectStreamToVideo = function () {
        if (!this.playableStreams || !this.videoElement) {
            return;
        }
        if (this.playableStreams.length > 0) {
            // Use the first PlayableStream for now
            // Later, we can use the others as fallback
            this.attachedStream = this.playableStreams[0];
            this.attachedStream.attach(this.videoElement, this.initialBitrate);
        }
    };
    Vidi.prototype.detachCurrentStream = function () {
        if (this.attachedStream && this.videoElement) {
            this.attachedStream.detach(this.videoElement);
            this.attachedStream = null;
        }
    };
    Vidi.prototype.addNativeVideoListeners = function (videoElement) {
        var _this = this;
        if (!videoElement) {
            return;
        }
        Object.keys(this.nativeEventHandlers).forEach(function (e) { return videoElement.addEventListener(e, _this.onNativeEvent); });
    };
    Vidi.prototype.removeNativeVideoListeners = function (videoElement) {
        var _this = this;
        if (!videoElement) {
            return;
        }
        Object.keys(this.nativeEventHandlers).forEach(function (e) { return videoElement.removeEventListener(e, _this.onNativeEvent); });
    };
    Vidi.prototype.onNativeEvent = function (event) {
        if (event.target !== this.videoElement) {
            return;
        }
        var handler = this.nativeEventHandlers[event.type];
        if (handler) {
            handler(event);
        }
        else {
            throw "Received a native event without an handler: " + event.type;
        }
    };
    return Vidi;
}(types_1.VidiEmitter));
Vidi.PlaybackStatus = types_1.PlaybackStatus;
Vidi.MediaStreamTypes = types_1.MediaStreamTypes;
Vidi.Errors = types_1.Errors;
exports.Vidi = Vidi;
//# sourceMappingURL=vidi.js.map


//////////////////
// WEBPACK FOOTER
// ./~/vidi/dist/src/vidi.js
// module id = 2776
// module chunks = 66